import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Ticket, Heart, ChevronDown, ArrowUpRight } from "lucide-react";
import EncoreWordmark from "./EncoreWordmark";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true, testid: "nav-home" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/gallery", label: "Gallery", testid: "nav-gallery" },
  { to: "/news", label: "News", testid: "nav-news" },
  { to: "/booking", label: "Booking", testid: "nav-booking" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-stone-950/80 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
        <Link
          to="/"
          data-testid="nav-logo"
          className="block hover:opacity-90 transition-opacity flex-shrink-0"
          aria-label="Encore Theatre and Gallery — Home"
        >
          <EncoreWordmark size="nav" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => {
            // About gets a hover-dropdown revealing Policies as a sub-item.
            if (l.to === "/about") {
              return (
                <div key={l.to} className="relative group" data-testid="nav-about-wrap">
                  <NavLink
                    to={l.to}
                    end={l.end}
                    data-testid={l.testid}
                    className={({ isActive }) =>
                      "nav-link inline-flex items-center gap-1.5" +
                      (isActive ? " active" : "")
                    }
                  >
                    {l.label}
                    <ChevronDown
                      size={12}
                      strokeWidth={2.5}
                      className="transition-transform duration-300 group-hover:rotate-180"
                    />
                  </NavLink>
                  <div
                    data-testid="nav-about-dropdown"
                    className="pointer-events-none group-hover:pointer-events-auto absolute left-0 top-full pt-3 w-64 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200"
                  >
                    <div className="bg-stone-950/95 backdrop-blur-xl border border-[#DE9E48]/40 rounded-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] overflow-hidden">
                      <NavLink
                        to="/about"
                        end
                        data-testid="nav-about-dropdown-about"
                        className="block px-5 py-3 font-body text-sm text-stone-300 hover:text-[#DE9E48] hover:bg-white/5 transition-colors"
                      >
                        Our Story
                      </NavLink>
                      <NavLink
                        to="/history"
                        data-testid="nav-about-dropdown-history"
                        className="block px-5 py-3 border-t border-white/5 font-body text-sm text-stone-300 hover:text-[#DE9E48] hover:bg-white/5 transition-colors"
                      >
                        Our History
                      </NavLink>
                      <NavLink
                        to="/board"
                        data-testid="nav-about-dropdown-board"
                        className="block px-5 py-3 border-t border-white/5 font-body text-sm text-stone-300 hover:text-[#DE9E48] hover:bg-white/5 transition-colors"
                      >
                        Board of Directors
                      </NavLink>
                      <NavLink
                        to="/directions"
                        data-testid="nav-about-dropdown-directions"
                        className="block px-5 py-3 border-t border-white/5 font-body text-sm text-stone-300 hover:text-[#DE9E48] hover:bg-white/5 transition-colors"
                      >
                        Directions &amp; Parking
                      </NavLink>
                      <NavLink
                        to="/policies"
                        data-testid="nav-about-dropdown-policies"
                        className="block px-5 py-3 border-t border-white/5 font-body text-sm text-stone-300 hover:text-[#DE9E48] hover:bg-white/5 transition-colors"
                      >
                        Policies &amp; Procedures
                      </NavLink>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) =>
                  "nav-link" + (isActive ? " active" : "")
                }
              >
                {l.label}
              </NavLink>
            );
          })}
          <div className="relative group" data-testid="nav-tickets-wrap">
            <a
              data-testid="nav-tickets"
              href="https://encorebham.ludus.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold !py-2.5 !px-5 text-[0.7rem]"
            >
              <Ticket size={14} />
              Tickets
              <ChevronDown
                size={12}
                strokeWidth={2.5}
                className="transition-transform duration-300 group-hover:rotate-180"
              />
            </a>
            {/* Hover dropdown — special events */}
            <div
              data-testid="nav-tickets-dropdown"
              className="pointer-events-none group-hover:pointer-events-auto absolute right-0 top-full pt-3 w-80 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200"
            >
              <div className="bg-stone-950/95 backdrop-blur-xl border border-[#DE9E48]/40 rounded-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] overflow-hidden">
                <p className="font-overline text-[0.55rem] tracking-[0.28em] text-[#DE9E48] px-5 pt-4 pb-2">
                  — Special Events
                </p>
                <Link
                  to="/events/i-stay-with-me"
                  data-testid="nav-tickets-dropdown-istay"
                  className="group/item block px-5 py-4 hover:bg-[#DE9E48]/10 border-t border-white/5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-[1.05rem] leading-tight tracking-tight text-[#FDFBF7] group-hover/item:text-[#DE9E48] transition-colors">
                        I Stay With Me
                      </p>
                      <p className="font-body text-[0.7rem] text-stone-400 mt-1 leading-snug normal-case tracking-normal">
                        Sep 19 &amp; 20 · One-woman show by Markeitra Gilliam
                      </p>
                    </div>
                    <ArrowUpRight
                      size={14}
                      strokeWidth={1.75}
                      className="text-stone-500 group-hover/item:text-[#DE9E48] group-hover/item:-translate-y-0.5 group-hover/item:translate-x-0.5 transition-all flex-shrink-0 mt-1"
                    />
                  </div>
                </Link>
                <a
                  href="https://encorebham.ludus.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-tickets-dropdown-ludus"
                  className="block px-5 py-3 border-t border-white/5 font-overline text-[0.65rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] hover:bg-white/5 transition-colors"
                >
                  Full Ticket Schedule →
                </a>
              </div>
            </div>
          </div>
          <a
            data-testid="nav-donate"
            href="https://www.paypal.com/donate/?hosted_button_id=DJQ4S2EYTXKGS"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline !py-2.5 !px-5 text-[0.7rem] !border-[#DE9E48]/60 !text-[#DE9E48] hover:!bg-[#7A431D] hover:!text-white hover:!border-[#7A431D]"
          >
            <Heart size={14} />
            Donate
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#FDFBF7]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-stone-950/98 backdrop-blur-xl border-t border-white/10"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  "nav-link text-base" + (isActive ? " active" : "")
                }
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/history"
              data-testid="nav-history-mobile"
              className="font-body text-sm text-stone-400 hover:text-[#DE9E48] self-start -mt-3 ml-4"
            >
              → Our History
            </NavLink>
            <NavLink
              to="/board"
              data-testid="nav-board-mobile"
              className="font-body text-sm text-stone-400 hover:text-[#DE9E48] self-start -mt-3 ml-4"
            >
              → Board of Directors
            </NavLink>
            <NavLink
              to="/directions"
              data-testid="nav-directions-mobile"
              className="font-body text-sm text-stone-400 hover:text-[#DE9E48] self-start -mt-3 ml-4"
            >
              → Directions &amp; Parking
            </NavLink>
            <NavLink
              to="/policies"
              data-testid="nav-policies-mobile"
              className="font-body text-sm text-stone-400 hover:text-[#DE9E48] self-start -mt-3 ml-4"
            >
              → Policies &amp; Procedures
            </NavLink>
            <a
              data-testid="nav-tickets-mobile"
              href="https://encorebham.ludus.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold self-start"
            >
              <Ticket size={14} />
              Get Tickets
            </a>
            <Link
              to="/events/i-stay-with-me"
              data-testid="nav-tickets-mobile-istay"
              className="font-body text-sm text-stone-300 hover:text-[#DE9E48] self-start"
            >
              → I Stay With Me · Sep 19-20
            </Link>
            <a
              data-testid="nav-donate-mobile"
              href="https://www.paypal.com/donate/?hosted_button_id=DJQ4S2EYTXKGS"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline self-start !border-[#DE9E48]/60 !text-[#DE9E48]"
            >
              <Heart size={14} />
              Donate
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
